import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

// Get token from localStorage
const token = localStorage.getItem('token');

export const getBusinessCentreCredentialsAndCreateToken = async (connectorId, mcId, masterConnectors) => {
  try {
    // Find the Business Centre master connector details
    const businessCentreConnector = masterConnectors.find(mc => 
      (mc.mcRecId?.toString() === mcId?.toString() || 
       mc.mcId?.toString() === mcId?.toString()) && 
      mc.mcName === "Dynamics Business Central"
    );
    
    if (!businessCentreConnector) {
      throw new Error('Business Centre master connector not found');
    }

    // Fetch client ID and client secret from the API endpoint using connector instance ID (ctd_id)
    const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connectorId), {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });
    if (!paramResponse.ok) {
      throw new Error('Failed to fetch connector parameters');
    }
    const connectorParams = await paramResponse.json();
    
    const clientId = connectorParams.find(param => param.scpName === "clientid")?.scpValue;
    if (!clientId) {
      throw new Error('Client ID not found in connector parameters');
    }

    // Store the connector ID for after redirect
    localStorage.setItem('PendingBusinessCentreConnectorId', connectorId);
    localStorage.setItem('ConnectorType', 'BusinessCentre');

    // DynamicsBusinessCentral OAuth 2.0 Authentication URL with dynamic client ID
    // Use Master Connector auth URL
    const authEndpoint = businessCentreConnector.mcAuthurl;
    if (!authEndpoint) {
      throw new Error('Business Centre Authentication URL is missing from Master Connector data');
    }
    const businessCentreAuthUrl = `${authEndpoint}${authEndpoint.includes('?') ? '&' : '?'}client_id=${clientId}&response_type=code&redirect_uri=${process.env.REACT_APP_OAUTH_REDIRECT_URI}&scope=https://api.businesscentral.dynamics.com/.default offline_access&state=${connectorId}`;
    
    // Check if we already have a stored authorization code
    const storedAuthCode = localStorage.getItem('AuthCode');

    if (storedAuthCode) {
      // If we have a stored authorization code, exchange it for an access token
      const tokenResponse = await exchangeBusinessCentreAuthCodeForToken(
        storedAuthCode, 
        clientId,
        mcId
      );

      // Clean up the stored authorization code
      localStorage.removeItem('AuthCode');
      localStorage.removeItem('PendingBusinessCentreConnectorId');
      localStorage.removeItem('ConnectorType');

      // Return the access token
      return tokenResponse;
    } else {
      // Redirect to DynamicsBusinessCentral authentication
      window.location.href = businessCentreAuthUrl;
      // Return null since we're redirecting
      return null;
    }
  } catch (error) {
    console.error('Error getting DynamicsBusinessCentral credentials:', error);
    throw error;
  }
};

// Helper function to exchange authorization code for DynamicsBusinessCentral token
export const exchangeBusinessCentreAuthCodeForToken = async (authCode, clientId, mcId) => {
  try {
    const tokenUrl = `${process.env.REACT_APP_BACKEND_URL}synctrack/businesscentral/authToken`;
    
    const response = await fetch(`${tokenUrl}?code=${encodeURIComponent(authCode)}&mcId=${encodeURIComponent(mcId)}`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange error response:', errorText);
      throw new Error(`Failed to exchange authorization code for token: ${response.status}`);
    }

    const tokenData = await response.json();
    
    // Get the connector ID that was stored before redirect
    const connectorId = localStorage.getItem('PendingBusinessCentreConnectorId');
    
    if (connectorId) {
      // Update the connector with token data
      const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors`;
      
      try {
        // First, get the current connector data
        const connectorResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        });
        if (!connectorResponse.ok) {
          throw new Error(`Failed to fetch connector data: ${connectorResponse.status}`);
        }
        const connectorData = await connectorResponse.json();
        
        // Calculate token expiry dates
        const tokenExpiry = new Date(Date.now() + tokenData.expires_in * 1000).toISOString();
        
        // Calculate the refresh token expiry (long-lived, typically 90 days for Microsoft)
        const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
        const refreshTokenExpiry = new Date(Date.now() + NINETY_DAYS_MS).toISOString();
        
        // Prepare updated connector data
        const updatedConnectorData = {
          ...connectorData,
          token: tokenData.access_token,
          refreshToken: tokenData.refresh_token,
          tokenExpiry: tokenExpiry,
          refreshUrl: connectorData.refreshUrl,
          refreshTokenExpiry: refreshTokenExpiry,
          status: 'Connected',
          connectedTime: new Date().toISOString()
        };
        
        // Make the PUT request to update the connector
        const updateResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(updatedConnectorData)
        });
        
        if (!updateResponse.ok) {
          const errorText = await updateResponse.text();
          console.error('Update connector error:', errorText);
          throw new Error(`Failed to update connector with tokens: ${updateResponse.status}`);
        }
        
        console.log('Connector updated successfully with tokens');
        window.location.reload();
      } catch (error) {
        console.error('Error updating connector with tokens:', error);
      }
    }
    return tokenData;
  } catch (error) {
    console.error('Token exchange error:', error);
    throw error;
  }
};